import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import { useApp } from '@/lib/provider/AppProvider'
import { useAuth } from '@/lib/provider/AuthProvider'
import { useTenant } from '@/lib/provider/TenantProvider'
import { cn } from '@/lib/misc/Utils'

const CommandPalette = lazy(() => import('./CommandPalette'))

export const COMPANY_PROFILE_PATH = '/settings/company-profile'

/**
 * The organization the navigation should name. While a customer's portal is
 * being viewed, that is the customer — not the signed-in user's own company,
 * which stays put for the whole impersonation.
 */
export const useOrganizationName = (): string => {
  const [auth] = useAuth()
  const { customerId, tenantName } = useTenant()

  // The tenant's profile is fetched, so its name is briefly unknown. Falling
  // back to the user's own company there would name the wrong organization.
  if (customerId) return tenantName ?? '…'

  return auth.user.company?.name ?? 'N/A'
}

interface OrganizationButtonProps {
  className?: string
  /** Applied while the impersonation palette is open. */
  activeClassName?: string
  children?: (organizationName: string) => React.ReactNode
}

/**
 * The organization name in the navigation. Staff with the superuser palette get
 * the impersonation search; everyone else goes to the company profile.
 * Owns the palette (and its ⌘K shortcut) so both nav layouts get it.
 */
const OrganizationButton = ({
  className,
  activeClassName,
  children,
}: OrganizationButtonProps) => {
  const [appState] = useApp()
  const [auth] = useAuth()
  const navigate = useNavigate()
  const [isPaletteOpen, setIsPaletteOpen] = useState(false)

  const organizationName = useOrganizationName()

  const canImpersonate = useMemo(
    () =>
      Boolean(auth.user.show_company_info) &&
      Boolean(appState.waffleFlags?.includes('superuser_command_palette')),
    [auth.user.show_company_info, appState.waffleFlags]
  )

  const onActivate = useCallback(() => {
    if (canImpersonate) {
      setIsPaletteOpen((open) => !open)
      return
    }
    navigate(COMPANY_PROFILE_PATH)
  }, [canImpersonate, navigate])

  useEffect(() => {
    if (!canImpersonate) return

    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        setIsPaletteOpen((open) => !open)
      } else if (event.key === 'Escape') {
        setIsPaletteOpen(false)
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [canImpersonate])

  return (
    <>
      <button
        type="button"
        data-cy="organization-button"
        onClick={onActivate}
        title={
          canImpersonate
            ? `${organizationName} — switch customer`
            : `${organizationName} — company profile`
        }
        className={cn(className, isPaletteOpen && activeClassName)}
      >
        {children ? (
          children(organizationName)
        ) : (
          <span className="max-w-[140px] truncate">{organizationName}</span>
        )}
      </button>

      {isPaletteOpen &&
        createPortal(
          <Suspense fallback={null}>
            <CommandPalette onClose={() => setIsPaletteOpen(false)} />
          </Suspense>,
          document.body
        )}
    </>
  )
}

export default OrganizationButton
